'use client'

import { useState } from 'react'

export type GrowthChartData = {
  title: string
  badge: string
  bars: number[]
}

interface HyperGrowthChartProps {
  chart: GrowthChartData
}

export function HyperGrowthChart({ chart }: HyperGrowthChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  const { title, badge, bars } = chart
  const maxVal = Math.max(...bars, 1)

  // Parse numeric badge target (e.g. "1,150" -> 1150)
  const numericBadge = parseFloat(badge.replace(/[^\d.]/g, '')) || maxVal

  const getDisplayValue = (val: number, idx: number, isLast: boolean) => {
    if (isLast && badge) return badge
    // If the input values are already real store counts (> 100), show them directly
    if (val > 100 && maxVal > 100) return Math.round(val).toLocaleString('vi-VN')
    // Otherwise calculate proportional value from the badge
    const proportional = Math.round((val / maxVal) * numericBadge)
    return proportional.toLocaleString('vi-VN')
  }

  return (
    <div className="mt-8 mb-10">
      <div className="text-[10px] sm:text-[11px] font-mono font-bold text-white/75 uppercase tracking-widest mb-3">
        {title}
      </div>

      {/* Container mở rộng bằng độ rộng tiêu đề/đoạn văn max-w-md */}
      <div className="pt-6 w-full max-w-md sm:max-w-[460px]">
        <div className="flex items-end gap-2.5 sm:gap-3.5 h-16 sm:h-20 w-full">
          {bars.map((val, idx) => {
            const isLast = idx === bars.length - 1
            const isHovered = hoveredIdx === idx
            // Show badge if this bar is hovered OR if nothing is hovered and this is the last bar
            const showValue = isHovered || (hoveredIdx === null && isLast)

            // Calculate height percentage: minimum 20%, maximum 100%
            const heightPercent = Math.max(20, Math.min(100, Math.round((val / maxVal) * 100)))

            // Opacity progression for bars
            const opacities = [0.18, 0.32, 0.48, 0.68, 1]
            const barOpacity = opacities[idx] ?? (0.2 + (idx / (bars.length - 1 || 1)) * 0.8)

            const displayValue = getDisplayValue(val, idx, isLast)

            return (
              <div
                key={idx}
                className="flex-1 h-full flex flex-col justify-end items-center relative group cursor-pointer"
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {/* Floating Value Badge - Absolutely positioned ABOVE the bar so it never distorts bar height */}
                <div
                  style={{ bottom: `${heightPercent}%` }}
                  className={`absolute mb-1.5 transition-all duration-200 pointer-events-none z-20 ${
                    showValue ? 'opacity-100 scale-100 -translate-y-0' : 'opacity-0 scale-90 translate-y-1'
                  }`}
                >
                  <span className="text-[11px] font-mono font-bold text-white whitespace-nowrap drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                    {displayValue}
                  </span>
                </div>

                {/* The Bar Itself */}
                <div
                  style={{
                    height: `${heightPercent}%`,
                    backgroundColor: isLast
                      ? '#ffffff'
                      : `rgba(255, 255, 255, ${isHovered ? Math.min(1, barOpacity + 0.2) : barOpacity})`,
                  }}
                  className={`w-full rounded-[2px] transition-all duration-300 ${
                    isLast
                      ? 'shadow-[0_0_8px_rgba(255,255,255,0.3)]'
                      : isHovered
                      ? 'shadow-[0_0_6px_rgba(255,255,255,0.25)]'
                      : ''
                  }`}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
